import * as React from "react";

import { StokedBarGroup } from "../../../../../parts/graph/stocked-bar-graph";
import { UserTable } from "./user-table";
import { Constant } from "../../../../../../../../server/analytics/v3/output/demand-forecast/aggregate";
import { GoalTable } from "../../../simulation/result/goal-table";
import { CurrentProductData } from "../../../../../../../../server/types/request/report/simulation";
import { CorrectionValue } from "../../../../../../../../server/models/simulation-value";
import { changeCorrectionValues } from "../../../../../../../../server/lib/common";
import { ResultTable } from "./result-table";
import { makeSimulator } from "../../../../../../../../shared/simulation";
import { SellPotentialValue } from "../../../simulation/result/result-table";
import { StockedBarGraphLatest } from "../../../../../parts/graph/stocked-bar-graph-latest";
import { CPCorrectionValueTable } from "./cp-correction-value";
import {
	SimulationPredictionData,
	SimulationReferenceSourceData,
} from "../../../../../../../../server/types/request/simulation";

type Props = {
	constValue: Constant;
	correctionValue: CorrectionValue;
	data: SimulationPredictionData;
	goalSalesVolume: number;
	goalRecognitionRate: number;
	referenceSourceData: SimulationReferenceSourceData;
	currentProduct: CurrentProductData;
};

export const SimulationResult = React.memo((props: Props) => {
	const { constValue, currentProduct, data, goalSalesVolume, goalRecognitionRate, referenceSourceData } = props;

	const { correctionValue, initialValue } = data;

	const sellPotentials = React.useMemo<SellPotentialValue[]>(() => {
		const { calc } = makeSimulator(referenceSourceData, currentProduct?.initialValue, constValue, goalRecognitionRate);
		const isCPChange = currentProduct?.correctionValue?.correctionFactor != null;
		const cpResult = calc(initialValue.purchaseIntent, {}, currentProduct?.correctionValue);
		const result = calc(initialValue.purchaseIntent, correctionValue, currentProduct?.correctionValue);
		const changed = changeCorrectionValues(correctionValue, referenceSourceData, goalRecognitionRate);

		const ret: SellPotentialValue[] = [
			{
				label: "初期値（万個）",
				sellPotential: {
					total: initialValue.total,
					trial: initialValue.trial,
					repeat: initialValue.repeat,
					localOther: initialValue.localOther,
					inbound: initialValue.ibby,
				},
			},
			{
				label: "①実績値修正後\n（万個）",
				sellPotential: isCPChange
					? {
							total: cpResult.total,
							trial: cpResult.trial,
							repeat: cpResult.repeat,
							localOther: cpResult.localOther,
							inbound: cpResult.ibby,
					  }
					: { total: undefined, trial: undefined, repeat: undefined, localOther: undefined, inbound: undefined },
			},
			{
				label: "②テスト品 変数補整後\n（万個）",
				sellPotential: changed
					? {
							total: result.total,
							trial: result.trial,
							repeat: result.repeat,
							localOther: result.localOther,
							inbound: result.ibby,
					  }
					: {
							total: undefined,
							trial: undefined,
							repeat: undefined,
							localOther: undefined,
							inbound: undefined,
					  },
			},
		];
		return ret;
	}, [referenceSourceData, currentProduct, constValue, goalRecognitionRate, initialValue, correctionValue]);

	const salesData = React.useMemo<StokedBarGroup[]>(() => {
		return sellPotentials.map(({ label, sellPotential }) => ({
			bars: [
				{ color: "#f56302", label: "トライアル", value: sellPotential.trial },
				{ color: "#ffc000", label: "リピート", value: sellPotential.repeat },
				{ color: "#83db4d", label: "ローカルその他", value: sellPotential.localOther },
				{ color: "#6492e3", label: "インバウンド", value: sellPotential.inbound },
			],
			label,
			total: sellPotential.total,
		}));
	}, [sellPotentials]);

	const graphBase = React.useMemo(() => {
		const tmp = Math.max(...salesData.map((d) => d.bars.reduce((a, b) => a + (b.value || 0), 0)), goalSalesVolume);

		if (tmp < 10) {
			const max = Math.floor(tmp + 1);

			return {
				max,
				step: Math.floor((max / 10) * 100) / 100,
			};
		}

		const max = Math.ceil(Math.round(tmp / 10) + 1) * 10;

		let step = Math.ceil(max / 10);

		if (5 < step && step < 10) {
			step = 10;
		} else if (step % 10 !== 0) {
			step = Math.ceil(step / 10) * 10;
		}

		return {
			max,
			step,
		};
	}, [goalSalesVolume, salesData]);

	return (
		<table className="wrap-table">
			<tbody>
				<tr>
					<td className="table-area">
						{/* 基人口、購入意向ワンナンバースコア、定数テーブル */}
						<UserTable
							constValue={constValue}
							segmentPurchaseIntent={data.initialValue.purchaseIntent}
							user={referenceSourceData.user}
						/>

						{/* 判定基準テーブル */}
						<GoalTable goalSalesVolume={goalSalesVolume} />

						{/* 実績補整係数テーブル */}
						{(currentProduct?.correctionValue?.correctionFactor != null ||
							currentProduct?.initialValue?.correctionFactor != null) && (
							<CPCorrectionValueTable
								cpCorrectionValue={
									currentProduct.correctionValue.correctionFactor ?? currentProduct.initialValue.correctionFactor
								}
							/>
						)}

						{/* 推定値テーブル */}
						<ResultTable goalSalesVolume={goalSalesVolume} sellPotentials={sellPotentials} />
					</td>

					{/* グラフ */}
					<td>
						<StockedBarGraphLatest
							barGroups={salesData}
							lineValue={goalSalesVolume}
							max={graphBase.max}
							step={graphBase.step}
						/>
					</td>
				</tr>
			</tbody>
		</table>
	);
});
